import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Users, Calendar, Newspaper, HandHeart, Mail, Send, Image as ImageIcon, MapPin, Layers, Settings as SettingsIcon, Award, FileText, Shield, UserCheck, Crown, BookOpen } from 'lucide-react';
import LeadersEditor from '@/components/admin/LeadersEditor';
import GenericEditor from '@/components/admin/GenericEditor';
import DepartmentsEditor from '@/components/admin/DepartmentsEditor';
import SettingsEditor from '@/components/admin/SettingsEditor';
import UsersEditor from '@/components/admin/UsersEditor';
import PageContentEditor from '@/components/admin/PageContentEditor';
import WomensAdminEditor from '@/components/admin/WomensAdminEditor';
import GalleryRowsEditor from '@/components/admin/GalleryRowsEditor';
import UniversalPhotoBlocks from '@/components/admin/UniversalPhotoBlocks';
import MainHistoryEditor from '@/components/admin/MainHistoryEditor';
import { BioEditor, LegacyEditor } from '@/components/admin/BioEditors';
import LinkListPanel from '@/components/editable/LinkListEditor';
import { DEFAULT_QUICK_LINKS, defaultGivingLinks } from '@/data/linkDefaults';
import { useSiteData } from '@/context/SiteDataContext';

function LinksAdmin() {
  const { settings } = useSiteData();
  if (!settings) return null;
  return (
    <div className="space-y-6">
      <LinkListPanel settingsKey="footer_links" defaults={DEFAULT_QUICK_LINKS} testid="admin-quick-links" title="Quick Links (site footer)"
        help="Add, rename, drag to reorder, untick Show to hide, or remove links. Removing a link never deletes the page." />
      <LinkListPanel settingsKey="giving_links" defaults={defaultGivingLinks(settings.givelify_url)} testid="admin-giving-links" title="Giving Page Buttons"
        help="The giving buttons on /giving. Edit names and destinations (e.g. a Givelify URL, or #give-by-mail to jump to the mail section)." />
    </div>
  );
}

// Field configs extracted to module scope to avoid re-creating arrays on every render.
const CHURCH_FIELDS = [
  { key: 'name', label: 'Church Name', type: 'text' },
  { key: 'pastor', label: 'Pastor', type: 'text' },
  { key: 'address', label: 'Address (autocomplete)', type: 'address' },
  { key: 'phone', label: 'Phone', type: 'text' },
  { key: 'services', label: 'Service Times', type: 'text' },
  { key: 'lat', label: 'Latitude (auto-filled)', type: 'number', step: '0.0001' },
  { key: 'lng', label: 'Longitude (auto-filled)', type: 'number', step: '0.0001' },
];
const GALLERY_FIELDS = [
  { key: 'url', label: 'Photo', type: 'photo', photoCategory: 'gallery' },
  { key: 'caption', label: 'Caption (optional)', type: 'text' },
];
const EVENT_FIELDS = [
  { key: 'title', label: 'Title', type: 'text' },
  { key: 'description', label: 'Description', type: 'textarea' },
  { key: 'date', label: 'Date', type: 'date' },
  { key: 'time', label: 'Time', type: 'text' },
  { key: 'location', label: 'Location', type: 'text' },
  { key: 'department', label: 'Department', type: 'text' },
  { key: 'image', label: 'Cover Image', type: 'photo', photoCategory: 'events' },
  { key: 'bg_color', label: 'Card background color', type: 'color' },
  { key: 'registration_url', label: 'Registration URL (optional)', type: 'text' },
  { key: 'registration_label', label: 'Registration button text (default "Register")', type: 'text' },
  { key: 'featured', label: 'Feature on the homepage', type: 'checkbox' },
];
const NEWS_FIELDS = [
  { key: 'title', label: 'Title', type: 'text' },
  { key: 'excerpt', label: 'Excerpt', type: 'textarea', rows: 2 },
  { key: 'body', label: 'Body', type: 'textarea', rows: 8 },
  { key: 'image', label: 'Cover Image', type: 'photo', photoCategory: 'news' },
  { key: 'category', label: 'Category', type: 'text' },
  { key: 'author', label: 'Author', type: 'text' },
];
const TIMELINE_FIELDS = [
  { key: 'year', label: 'Year', type: 'text' },
  { key: 'title', label: 'Milestone', type: 'text' },
  { key: 'text', label: 'Description', type: 'textarea' },
];
const PAGE_FIELDS = [
  { key: 'title', label: 'Title', type: 'text' },
  { key: 'slug', label: 'URL slug (e.g. "our-heritage")', type: 'text' },
  { key: 'nav_label', label: 'Nav label (blank = hide from nav)', type: 'text' },
  { key: 'overline', label: 'Overline (small text above title)', type: 'text' },
  { key: 'subtitle', label: 'Subtitle', type: 'textarea', rows: 2 },
  { key: 'hero_image', label: 'Hero Image', type: 'photo', photoCategory: 'pages' },
  { key: 'body', label: 'Body content (plain text or basic markdown)', type: 'textarea', rows: 12 },
];
const DISTRICT_FIELDS = [
  { key: 'name', label: 'District Name', type: 'text' },
  { key: 'description', label: 'Description', type: 'textarea' },
  { key: 'image', label: 'Cover Image', type: 'photo', photoCategory: 'districts' },
  { key: 'superintendent', label: 'District Superintendent', type: 'text' },
  { key: 'superintendent_email', label: 'Superintendent Email', type: 'text' },
  { key: 'superintendent_phone', label: 'Superintendent Phone', type: 'text' },
  { key: 'chairlady', label: 'District Missionary / Chairlady', type: 'text' },
  { key: 'chairlady_email', label: 'Chairlady Email', type: 'text' },
  { key: 'chairlady_phone', label: 'Chairlady Phone', type: 'text' },
];
const NAV_FIELDS = [
  { key: 'label', label: 'Label (shown in menu)', type: 'text' },
  { key: 'href', label: 'URL / Path', type: 'text' },
  { key: 'parent_id', label: 'Nest under (leave blank for top-level)', type: 'nav_parent' },
  { key: 'dropdown_label', label: 'Name of this item\'s own link inside its dropdown (blank = same as label)', type: 'text' },
  { key: 'show_parent_in_dropdown', label: 'Show this item\'s own link at the top of its dropdown', type: 'boolean' },
  { key: 'visible', label: 'Visible in menu (uncheck to hide without deleting)', type: 'boolean' },
];

function NavigationEditor() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get('/site/nav').then(r => setItems(r.data || [])).catch(() => {}); }, []);
  const hidden = items.filter(i => i.visible === false);
  return (
    <>
      {hidden.length > 0 && (
        <div className="bg-amber-50 border-l-4 border-amber-500 rounded p-4 mb-4">
          <div className="text-sm font-bold text-amber-900 mb-2">Hidden Tabs ({hidden.length})</div>
          <div className="text-xs text-amber-900/80 mb-2">These tabs are configured but not visible in the main navigation. Click "Edit" on any row below and re-check "Visible" to bring it back.</div>
          <div className="flex flex-wrap gap-2">
            {hidden.map(h => (
              <span key={h.id} data-testid={`hidden-nav-${h.id}`} className="inline-flex items-center gap-1 bg-white border border-amber-300 rounded-full px-3 py-1 text-xs">
                <span className="font-semibold">{h.label || '(unnamed)'}</span>
                <span className="text-charcoal-muted">→ {h.href}</span>
              </span>
            ))}
          </div>
        </div>
      )}
      <GenericEditor title="Main Navigation" endpoint="/site/nav" category="nav" imageKey={null}
        fields={NAV_FIELDS} />
    </>
  );
}
const WOMENS_BRANCH_FIELDS = [
  { key: 'name', label: 'Branch Name', type: 'text' },
  { key: 'slug', label: 'Slug / short key (e.g. "missionary")', type: 'text' },
  { key: 'tagline', label: 'Tagline / Motto', type: 'text' },
  { key: 'logo', label: 'Branch Logo (square)', type: 'photo', photoCategory: 'womens' },
  { key: 'image', label: 'Cover Image (banner)', type: 'photo', photoCategory: 'womens' },
  { key: 'leader_name', label: 'Branch Leader — Name', type: 'text' },
  { key: 'leader_title', label: 'Branch Leader — Official Title', type: 'text' },
  { key: 'leader_photo', label: 'Branch Leader — Photo', type: 'photo', photoCategory: 'womens' },
  { key: 'description', label: 'Description', type: 'textarea', rows: 6 },
  { key: 'mission', label: 'Mission / Purpose', type: 'textarea', rows: 3 },
  { key: 'email', label: 'Contact Email', type: 'text' },
  { key: 'phone', label: 'Contact Phone', type: 'text' },
  { key: 'address', label: 'Meeting Address (optional)', type: 'text' },
];

const ALL_TABS = [
  { id: 'overview',    label: 'Overview',    icon: Award,       roles: ['admin', 'prelate', 'bishop', 'secretary', 'cfo', 'dept_leader'] },
  { id: 'leaders',     label: 'Leadership',  icon: Users,       roles: ['admin', 'prelate', 'bishop'] },
  { id: 'departments', label: 'Departments', icon: Layers,      roles: ['admin', 'prelate', 'dept_leader'] },
  { id: 'churches',    label: 'Churches',    icon: MapPin,      roles: ['admin', 'prelate'] },
  { id: 'gallery',     label: 'Gallery',     icon: ImageIcon,   roles: ['admin', 'prelate', 'secretary', 'cfo'] },
  { id: 'gallery_rows_main',   label: 'Gallery Rows · Main',    icon: ImageIcon, roles: ['admin', 'prelate'] },
  { id: 'gallery_rows_womens', label: "Gallery Rows · Women's", icon: ImageIcon, roles: ['admin', 'prelate'] },
  { id: 'photo_blocks',        label: 'Universal Photo Blocks', icon: ImageIcon, roles: ['admin', 'prelate'] },
  { id: 'main_history',        label: 'History & Story (Main)', icon: BookOpen,  roles: ['admin', 'prelate'] },
  { id: 'prelate_bio',         label: 'Prelate Bio Page',       icon: Crown,     roles: ['admin', 'prelate'] },
  { id: 'supervisor_bio',      label: 'Supervisor Bio Page',    icon: Crown,     roles: ['admin', 'prelate'] },
  { id: 'first_lady_bio',      label: 'First Lady Bio Page',    icon: Crown,     roles: ['admin', 'prelate'] },
  { id: 'legacy',              label: 'Legacy (Former Leaders)',icon: Award,     roles: ['admin', 'prelate'] },
  { id: 'events',      label: 'Events',      icon: Calendar,    roles: ['admin', 'prelate', 'bishop', 'secretary', 'dept_leader'] },
  { id: 'news',        label: 'News',        icon: Newspaper,   roles: ['admin', 'prelate', 'bishop', 'secretary'] },
  { id: 'timeline',    label: 'Timeline',    icon: Award,       roles: ['admin', 'prelate', 'bishop', 'secretary'] },
  { id: 'pages',       label: 'Custom Pages',icon: FileText,    roles: ['admin', 'prelate'] },
  { id: 'page_content',label: 'Page Content',icon: FileText,    roles: ['admin', 'prelate'] },
  { id: 'navigation',  label: 'Navigation',  icon: Layers,      roles: ['admin', 'prelate'] },
  { id: 'links',       label: 'Quick Links & Giving', icon: Layers, roles: ['admin', 'prelate'] },
  { id: 'districts',   label: 'Districts',   icon: MapPin,      roles: ['admin', 'prelate', 'bishop'] },
  { id: 'submissions', label: 'Submissions', icon: Award,       roles: ['admin', 'prelate'] },
  { id: 'role_claims', label: 'Role Claims', icon: UserCheck,   roles: ['admin', 'prelate'] },
  { id: 'womens_branches', label: "Women's Branches", icon: Crown, roles: ['admin', 'prelate'] },
  { id: 'womens_ministry', label: "Women's Ministry", icon: Crown, roles: ['admin', 'prelate'] },
  { id: 'prayers',     label: 'Prayers',     icon: HandHeart,   roles: ['admin', 'prelate', 'secretary'] },
  { id: 'contacts',    label: 'Contacts',    icon: Mail,        roles: ['admin', 'prelate', 'secretary', 'cfo'] },
  { id: 'settings',    label: 'Settings',    icon: SettingsIcon,roles: ['admin', 'prelate'] },
  { id: 'users',       label: 'Users & Roles', icon: Shield,    roles: ['admin', 'prelate'] },
];

export default function AdminDashboard() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('overview');
  const [stats, setStats] = useState(null);

  const ELEVATED = ['admin', 'prelate', 'bishop', 'secretary', 'cfo', 'dept_leader'];
  const allowedTabs = ALL_TABS.filter(t => t.roles.includes(user?.role));

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate('/login'); return; }
    if (!ELEVATED.includes(user.role)) { toast.error('Elevated role required'); navigate('/'); return; }
    api.get('/admin/stats').then(r => setStats(r.data)).catch(() => {});
  }, [user, loading, navigate]);

  useEffect(() => {
    if (allowedTabs.length && !allowedTabs.find(t => t.id === tab)) {
      setTab(allowedTabs[0].id);
    }
  }, [allowedTabs, tab]);

  if (loading || !user) return <div className="py-24 text-center">Loading…</div>;

  return (
    <div className="min-h-screen bg-[#F8F9FA] py-10">
      <div className="max-w-7xl mx-auto px-6">
        <div className="mb-8 flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="overline mb-1">Administration</div>
            <h1 className="font-serif text-4xl text-royal font-bold">Admin Dashboard</h1>
            <div className="text-sm text-charcoal-muted mt-1">Welcome, {user.name} · <span className="text-gold uppercase tracking-widest text-xs">{user.role}</span></div>
          </div>
        </div>

        <div className="flex gap-2 mb-8 flex-wrap">
          {allowedTabs.map(t => (
            <button key={t.id} data-testid={`admin-tab-${t.id}`} onClick={() => setTab(t.id)}
              className={`px-4 py-2 rounded-full text-sm font-semibold capitalize flex items-center gap-1.5 ${tab === t.id ? 'bg-royal text-white' : 'bg-white border border-gray-200 hover:border-royal'}`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        {tab === 'overview' && <Overview stats={stats} />}
        {tab === 'leaders' && <LeadersEditor />}
        {tab === 'departments' && <DepartmentsEditor />}
        {tab === 'churches' && (
          <GenericEditor title="Churches" endpoint="/site/churches" category="churches" imageKey={null}
            fields={CHURCH_FIELDS} />
        )}
        {tab === 'gallery' && (
          <GenericEditor title="Gallery" endpoint="/site/gallery" category="gallery" imageKey="url" bulkKey="url"
            fields={GALLERY_FIELDS} />
        )}
        {tab === 'gallery_rows_main' && (
          <>
            <div className="bg-white border rounded p-4 mb-4 text-sm text-charcoal-muted">
              <strong className="text-royal">Main site Gallery — mixed-portrait rows.</strong> Public URL: <code className="text-royal">/gallery</code>. Each row can have a different layout (3 equal, 1 big + 2 small, etc.).
            </div>
            <GalleryRowsEditor scope="main" title="Main Site Gallery Rows" />
          </>
        )}
        {tab === 'gallery_rows_womens' && (
          <>
            <div className="bg-white border rounded p-4 mb-4 text-sm text-charcoal-muted">
              <strong className="text-royal">Women's Ministry Gallery — mixed-portrait rows.</strong> Currently rendered on the Women's Home page below the Branches section (auto-appears once at least one row exists).
            </div>
            <GalleryRowsEditor scope="womens" title="Women's Gallery Rows" />
          </>
        )}
        {tab === 'photo_blocks' && <UniversalPhotoBlocks />}
        {tab === 'main_history' && (
          <>
            <div className="bg-white border rounded p-4 mb-4 text-sm text-charcoal-muted">
              <strong className="text-royal">Main Site History &amp; Story</strong> — appears at <code className="text-royal">/history</code>. Add the tab to the main navigation from Admin ▸ Navigation if needed.
            </div>
            <MainHistoryEditor />
          </>
        )}
        {tab === 'prelate_bio' && (
          <>
            <div className="bg-white border rounded p-4 mb-4 text-sm text-charcoal-muted">
              Public URL: <code className="text-royal">/about/prelate</code>. Edit the portrait, paragraphs, and optional YouTube video.
            </div>
            <BioEditor slug="prelate-bio" label="Our Prelate" />
          </>
        )}
        {tab === 'supervisor_bio' && (
          <>
            <div className="bg-white border rounded p-4 mb-4 text-sm text-charcoal-muted">
              Public URL: <code className="text-royal">/about/supervisor</code>.
            </div>
            <BioEditor slug="supervisor-bio" label="Supervisor" />
          </>
        )}
        {tab === 'first_lady_bio' && (
          <>
            <div className="bg-white border rounded p-4 mb-4 text-sm text-charcoal-muted">
              Public URL: <code className="text-royal">/about/first-lady</code>.
            </div>
            <BioEditor slug="first-lady-bio" label="First Lady" />
          </>
        )}
        {tab === 'legacy' && (
          <>
            <div className="bg-white border rounded p-4 mb-4 text-sm text-charcoal-muted">
              Public URL: <code className="text-royal">/legacy</code>. Honor former / departed leaders. Each has an optional YouTube video that can be hidden without deleting.
            </div>
            <LegacyEditor />
          </>
        )}
        {tab === 'events' && (
          <GenericEditor title="Events" endpoint="/events" category="events" imageKey="image"
            fields={EVENT_FIELDS} />
        )}
        {tab === 'news' && (
          <GenericEditor title="News" endpoint="/news" category="news" imageKey="image"
            fields={NEWS_FIELDS} />
        )}
        {tab === 'timeline' && (
          <GenericEditor title="Timeline Milestones" endpoint="/site/timeline" category="timeline" imageKey={null}
            fields={TIMELINE_FIELDS} />
        )}
        {tab === 'pages' && (
          <>
            <div className="bg-white border rounded p-4 mb-4 text-sm text-charcoal-muted">
              <strong className="text-royal">Custom Pages</strong> appear in the site navigation automatically. Each page has its own URL at <code className="text-royal">/pages/&#123;slug&#125;</code>.
            </div>
            <GenericEditor title="Custom Pages" endpoint="/site/pages" category="pages" imageKey="hero_image"
              fields={PAGE_FIELDS} />
          </>
        )}
        {tab === 'prayers' && <PrayersList />}
        {tab === 'contacts' && <ContactsList />}
        {tab === 'submissions' && <SubmissionsList />}
        {tab === 'role_claims' && <RoleClaimsList />}
        {tab === 'womens_branches' && (
          <>
            <div className="bg-white border rounded p-4 mb-4 text-sm text-charcoal-muted">
              <strong className="text-royal">Women's Department Branches</strong> — each branch appears in the dedicated Women's mini-site at <code className="text-royal">/womens/branches</code>. Use the <em>slug</em> field to link related events, news, and photos (they'll show up on that branch's page when tagged with the same word).
            </div>
            <GenericEditor title="Women's Branches" endpoint="/site/womens-branches" category="womens" imageKey="image"
              fields={WOMENS_BRANCH_FIELDS} />
          </>
        )}
        {tab === 'womens_ministry' && (
          <>
            <div className="bg-white border rounded p-4 mb-4 text-sm text-charcoal-muted">
              <strong className="text-royal">Women's Ministry Control Panel</strong> — edit the header, menu tabs, colors, fonts, branches, and footer of the Women's mini-site independently from the main site.
            </div>
            <WomensAdminEditor />
          </>
        )}
        {tab === 'districts' && (
          <GenericEditor title="Districts" endpoint="/site/districts" category="districts" imageKey="image"
            fields={DISTRICT_FIELDS} />
        )}
        {tab === 'settings' && <SettingsEditor />}
        {tab === 'users' && <UsersEditor />}
        {tab === 'page_content' && <PageContentEditor />}
        {tab === 'navigation' && <NavigationEditor />}
        {tab === 'links' && <LinksAdmin />}
      </div>
    </div>
  );
}

function Overview({ stats }) {
  const cards = [
    { icon: Users, label: 'Leaders', value: stats?.leaders ?? 0 },
    { icon: Layers, label: 'Departments', value: stats?.departments ?? 0 },
    { icon: MapPin, label: 'Churches', value: stats?.churches ?? 0 },
    { icon: ImageIcon, label: 'Gallery', value: stats?.gallery ?? 0 },
    { icon: Calendar, label: 'Events', value: stats?.events ?? 0 },
    { icon: Newspaper, label: 'News', value: stats?.news ?? 0 },
    { icon: HandHeart, label: 'Prayer', value: stats?.prayer_requests ?? 0 },
    { icon: Mail, label: 'Messages', value: stats?.contact_messages ?? 0 },
    { icon: Send, label: 'Newsletter', value: stats?.newsletter_subscribers ?? 0 },
    { icon: Users, label: 'Users', value: stats?.users ?? 0 },
  ];
  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
        {cards.map(c => (
          <div key={c.label} className="bg-white border rounded-lg p-5">
            <c.icon className="w-5 h-5 text-gold mb-3" />
            <div className="text-3xl font-serif text-royal font-bold">{c.value}</div>
            <div className="text-xs text-charcoal-muted mt-1">{c.label}</div>
          </div>
        ))}
      </div>
      <div className="bg-white border rounded-lg p-8">
        <h2 className="font-serif text-2xl text-royal font-bold mb-3">Manage your ministry site</h2>
        <p className="text-charcoal-muted">Use the tabs above to edit every section of the website — leadership profiles (with contact info & photo cropping), department pages, church directory, media gallery, events, news, timeline, custom pages, and site-wide settings (colors, fonts, hero video, logo, contact info). Assign roles to other members from the <strong>Users & Roles</strong> tab.</p>
      </div>
    </div>
  );
}

function PrayersList() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get('/prayer-requests').then(r => setItems(r.data)).catch(() => {}); }, []);
  return (
    <div className="space-y-3">
      <h3 className="font-serif text-xl text-royal font-bold mb-4">Prayer Requests ({items.length})</h3>
      {items.map(p => (
        <div key={p.id} className="bg-white border rounded p-5">
          <div className="font-semibold text-royal">{p.name} {p.is_private && <span className="text-xs text-gold ml-2">private</span>}</div>
          <div className="text-xs text-charcoal-muted">{p.email || 'No email'} · {new Date(p.created_at).toLocaleString()}</div>
          <p className="text-charcoal-muted mt-2">{p.request}</p>
        </div>
      ))}
      {!items.length && <div className="text-sm text-charcoal-muted">No prayer requests yet.</div>}
    </div>
  );
}

function ContactsList() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get('/contact').then(r => setItems(r.data)).catch(() => {}); }, []);
  return (
    <div className="space-y-3">
      <h3 className="font-serif text-xl text-royal font-bold mb-4">Contact Messages ({items.length})</h3>
      {items.map(m => (
        <div key={m.id} className="bg-white border rounded p-5">
          <div className="font-semibold text-royal">{m.subject}</div>
          <div className="text-xs text-charcoal-muted">{m.name} · {m.email} · {new Date(m.created_at).toLocaleString()}</div>
          <p className="text-charcoal-muted mt-2">{m.message}</p>
        </div>
      ))}
      {!items.length && <div className="text-sm text-charcoal-muted">No messages yet.</div>}
    </div>
  );
}

function SubmissionsList() {
  const [items, setItems] = useState([]);
  const load = () => api.get('/site/submissions').then(r => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const setStatus = async (id, status) => { await api.patch(`/site/submissions/${id}`, { status }); load(); };
  return (
    <div className="space-y-3">
      <h3 className="font-serif text-xl text-royal font-bold mb-4">Public Submissions ({items.length})</h3>
      <p className="text-sm text-charcoal-muted mb-4">Members can submit their photo/title/church info from the site; you approve or reject here.</p>
      {items.map(s => (
        <div key={s.id} className="bg-white border rounded p-5">
          <div className="flex justify-between items-start gap-4">
            <div className="flex-1">
              <div className="font-semibold text-royal">{s.type || 'Submission'}: {s.name || s.title || '(no name)'}</div>
              <div className="text-xs text-charcoal-muted">{s.submitter_email || 'Anonymous'} · {new Date(s.created_at).toLocaleString()}</div>
              <pre className="text-xs text-charcoal-muted mt-2 whitespace-pre-wrap font-sans">{JSON.stringify({ ...s, id: undefined, created_at: undefined, status: undefined }, null, 2)}</pre>
            </div>
            <div className="flex flex-col gap-2">
              <span className={`text-xs uppercase font-semibold px-2 py-1 rounded ${s.status === 'approved' ? 'bg-green-100 text-green-700' : s.status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>{s.status}</span>
              <button onClick={() => setStatus(s.id, 'approved')} className="text-xs px-3 py-1 bg-green-600 text-white rounded">Approve</button>
              <button onClick={() => setStatus(s.id, 'rejected')} className="text-xs px-3 py-1 bg-red-500 text-white rounded">Reject</button>
            </div>
          </div>
        </div>
      ))}
      {!items.length && <div className="text-sm text-charcoal-muted">No submissions yet.</div>}
    </div>
  );
}

function RoleClaimsList() {
  const [items, setItems] = useState([]);
  const [depts, setDepts] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [note, setNote] = useState({});
  const load = () => api.get('/admin/role-claims').then(r => setItems(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get('/site/departments').then(r => setDepts(r.data)).catch(() => {});
    api.get('/site/districts').then(r => setDistricts(r.data)).catch(() => {});
  }, []);
  const nameOf = (list, id) => list.find(x => x.id === id)?.name || '';
  const review = async (id, status) => {
    try {
      await api.patch(`/admin/role-claims/${id}`, { status, admin_note: note[id] || '' });
      toast.success(status === 'approved' ? 'Approved — role assigned' : 'Rejected');
      load();
    } catch (e) { toast.error('Action failed'); }
  };
  const pending = items.filter(i => i.status === 'pending');
  const other = items.filter(i => i.status !== 'pending');
  const renderItem = (c) => (
    <div key={c.id} data-testid={`role-claim-${c.id}`} className="bg-white border rounded p-5">
      <div className="flex justify-between items-start gap-4 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="font-serif text-royal text-lg font-bold">{c.user_name}</div>
          <div className="text-xs text-charcoal-muted">{c.user_email} · {new Date(c.created_at).toLocaleString()}</div>
          <div className="mt-3 grid sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
            <div><span className="text-charcoal-muted">Requested role:</span> <span className="text-royal font-semibold uppercase tracking-wider text-xs">{c.requested_role}</span></div>
            {c.position_title && <div><span className="text-charcoal-muted">Title:</span> {c.position_title}</div>}
            {c.department_id && <div><span className="text-charcoal-muted">Department:</span> {nameOf(depts, c.department_id)}</div>}
            {c.district_id && <div><span className="text-charcoal-muted">District:</span> {nameOf(districts, c.district_id)}</div>}
            {c.contact_phone && <div><span className="text-charcoal-muted">Phone:</span> {c.contact_phone}</div>}
          </div>
          {c.experience && <div className="mt-3 text-sm text-charcoal-muted bg-gray-50 border rounded p-3 whitespace-pre-wrap">{c.experience}</div>}
        </div>
        <div className="flex flex-col gap-2 min-w-[160px]">
          <span className={`text-xs uppercase font-semibold px-2 py-1 rounded text-center ${c.status === 'approved' ? 'bg-green-100 text-green-700' : c.status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>{c.status}</span>
          {c.status === 'pending' && (
            <>
              <input placeholder="Note (optional)" value={note[c.id] || ''} onChange={(e) => setNote({ ...note, [c.id]: e.target.value })} className="text-xs px-2 py-1 border rounded" />
              <button onClick={() => review(c.id, 'approved')} data-testid={`claim-approve-${c.id}`} className="text-xs px-3 py-1.5 bg-green-600 text-white rounded font-semibold">Approve & Assign</button>
              <button onClick={() => review(c.id, 'rejected')} data-testid={`claim-reject-${c.id}`} className="text-xs px-3 py-1.5 bg-red-500 text-white rounded font-semibold">Reject</button>
            </>
          )}
          {c.admin_note && c.status !== 'pending' && <div className="text-[11px] text-charcoal-muted italic">Note: {c.admin_note}</div>}
        </div>
      </div>
    </div>
  );
  return (
    <div className="space-y-3">
      <h3 className="font-serif text-xl text-royal font-bold mb-2">Position Claims ({items.length})</h3>
      <p className="text-sm text-charcoal-muted mb-4">Members request ministry roles from <code className="text-royal">/claim-position</code>. Approving assigns the requested role automatically.</p>
      {pending.length > 0 && <>
        <div className="text-xs uppercase text-gold font-bold tracking-widest">Pending ({pending.length})</div>
        {pending.map(renderItem)}
      </>}
      {other.length > 0 && <>
        <div className="text-xs uppercase text-charcoal-muted font-bold tracking-widest mt-6">Reviewed</div>
        {other.map(renderItem)}
      </>}
      {!items.length && <div className="text-sm text-charcoal-muted">No claims yet.</div>}
    </div>
  );
}
